import { useMutation } from "@tanstack/react-query";
import type { WayflyerSchemas } from "api-types";
import { useAxios } from "auth-sdk";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useCustomerAppConfig } from "undercarriage";

import { Fallback } from "../../fallback";

import { replaceWithInviteDestination } from "./replace-with-invite-destination";

const ACCEPT_INVITE_COOKIE_URL = "/api/wayflyer/invites/accept-cookie";

export const InviteCookieBootstrap = ({ children }: { children: ReactNode }) => {
  const {
    data: { hasInviteCookie, userEmail },
  } = useCustomerAppConfig();
  const { pathname } = useLocation();
  const isAuthPath = pathname.startsWith("/authn-wf/");
  const hasRequestedAcceptance = useRef(false);
  const [hasFinished, setHasFinished] = useState(false);
  const { mutate } = useAcceptInviteCookieMutation({
    onSuccess: ({ redirect_url }) => {
      if (redirect_url) {
        replaceWithInviteDestination(redirect_url);
      } else {
        setHasFinished(true);
      }
    },
    onError: () => setHasFinished(true),
  });

  useEffect(() => {
    if (!isAuthPath && userEmail && hasInviteCookie && !hasRequestedAcceptance.current) {
      hasRequestedAcceptance.current = true;
      mutate();
    }
  }, [hasInviteCookie, isAuthPath, mutate, userEmail]);

  if (isAuthPath || !userEmail || !hasInviteCookie || hasFinished) {
    return children;
  }

  return <Fallback />;
};

const useAcceptInviteCookieMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess: (data: WayflyerSchemas.InviteCookieAcceptanceSchema) => void;
  onError: () => void;
}) => {
  const axios = useAxios();

  return useMutation({
    mutationKey: ["accept-invite-cookie"],
    mutationFn: async () => {
      const { data } = await axios.post<WayflyerSchemas.InviteCookieAcceptanceSchema>(ACCEPT_INVITE_COOKIE_URL);
      return data;
    },
    retry: false,
    onSuccess,
    onError,
  });
};
